import { create } from "zustand";
import { persist } from "zustand/middleware";

export type Theme = "light" | "dark" | "system";

interface ThemeState {
    theme: Theme;
    resolvedTheme: "light" | "dark";
    setTheme: (theme: Theme) => void;
}

const STORAGE_KEY = "lyder-talk-theme";

const getSystemTheme = (): "light" | "dark" => {
    if (typeof window === "undefined") return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const resolveTheme = (theme: Theme): "light" | "dark" => {
    if (theme === "system") return getSystemTheme();
    return theme;
};

const applyTheme = (resolvedTheme: "light" | "dark") => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(resolvedTheme);
};

export const useThemeStore = create<ThemeState>()(
    persist(
        (set) => ({
            theme: "system" as Theme,
            resolvedTheme: "light" as const,
            setTheme: (newTheme: Theme) => {
                set({ theme: newTheme, resolvedTheme: resolveTheme(newTheme) });
            },
        }),
        {
            name: STORAGE_KEY,
            partialize: (state) => ({ theme: state.theme }),
        },
    ),
);

const stored = useThemeStore.getState();
const resolved = resolveTheme(stored.theme);
useThemeStore.setState({ resolvedTheme: resolved });
applyTheme(resolved);

useThemeStore.subscribe((state) => {
    applyTheme(state.resolvedTheme);
});

if (typeof window !== "undefined") {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", () => {
        const { theme } = useThemeStore.getState();
        if (theme === "system") {
            useThemeStore.setState({ resolvedTheme: resolveTheme("system") });
        }
    });
}

/**
 * Convenience hook returning the theme slice. Equivalent to selecting the
 * fields off `useThemeStore`; kept for ergonomic consumption across apps.
 */
export const useTheme = () => {
    const theme = useThemeStore((s) => s.theme);
    const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
    const setTheme = useThemeStore((s) => s.setTheme);
    return { theme, resolvedTheme, setTheme };
};
